/**
 * T-085 — Page 4: build run browser (spec §5).
 *
 * Lists GitHub Actions runs of `build-package-release.yml` (GET /api/runs,
 * server-cached, paginated) and, per run, the reconciled artifact inventory
 * from GCS `debs/run-<id>/` (GET /api/runs/:id/artifacts). Managed/direct
 * download links are issued straight from an artifact row through the same
 * POST /api/links endpoint and the spec §3 object-path allowlist — artifacts
 * whose object path is not an allowlisted GCS path (e.g. GitHub release asset
 * names) cannot be linked and are shown disabled.
 */

import { Fragment, useEffect, useState } from 'react';
import { isAllowlistedObjectPath } from '../../shared/api';
import type { Artifact, BuildRun, RunArtifactsResponse } from '../../shared/api';
import { sessionApi, useSessionToken } from '../state';
import { ErrorPanel, IssueLinkForm, TokenSignIn } from './AccessControlPage';

const PAGE_LIMIT = 25;

/* ------------------------------------------------------------------------ */
/* Formatting helpers (module-local)                                         */
/* ------------------------------------------------------------------------ */

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}

function formatBytes(size: number | null): string {
  if (size === null) return '—';
  if (size < 1024) return `${size} B`;
  const units = ['KiB', 'MiB', 'GiB'];
  let value = size;
  let unit = 'B';
  for (const next of units) {
    if (value < 1024) break;
    value /= 1024;
    unit = next;
  }
  return `${value.toFixed(1)} ${unit}`;
}

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (startedAt === null) return '—';
  const start = new Date(startedAt).getTime();
  const end = completedAt === null ? Date.now() : new Date(completedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return '—';
  const seconds = Math.round((end - start) / 1000);
  const minutes = Math.floor(seconds / 60);
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds % 60}s`;
}

/* ------------------------------------------------------------------------ */
/* Run status badge                                                          */
/* ------------------------------------------------------------------------ */

function RunStatusBadge(props: { run: BuildRun }) {
  const { run } = props;
  if (run.status !== 'completed') {
    return <span className="sg-badge sg-badge--info">{run.status.replace('_', ' ')}</span>;
  }
  switch (run.conclusion) {
    case 'success':
      return <span className="sg-badge sg-badge--success">success</span>;
    case 'failure':
    case 'timed_out':
      return <span className="sg-badge sg-badge--danger">{run.conclusion.replace('_', ' ')}</span>;
    case 'action_required':
      return <span className="sg-badge sg-badge--warning">action required</span>;
    default:
      return (
        <span className="sg-badge sg-badge--muted">{(run.conclusion ?? 'completed').replace('_', ' ')}</span>
      );
  }
}

/* ------------------------------------------------------------------------ */
/* Per-run artifact inventory (GET /api/runs/:id/artifacts)                  */
/* ------------------------------------------------------------------------ */

function RunArtifacts(props: { run: BuildRun }) {
  const { run } = props;
  const [result, setResult] = useState<RunArtifactsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);
  const [issueTarget, setIssueTarget] = useState<Artifact | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    sessionApi
      .listRunArtifacts(run.id, controller.signal)
      .then((response) => setResult(response))
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [run.id]);

  if (loading) return <p className="sg-muted">Loading artifacts for run {run.id}…</p>;

  return (
    <div className="sg-run-artifacts">
      <ErrorPanel error={error} onDismiss={() => setError(null)} />
      {result !== null && result.artifacts.length === 0 && (
        <p className="sg-muted">No artifacts recorded for this run.</p>
      )}
      {result !== null && result.artifacts.length > 0 && (
        <table className="sg-table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Python ABI</th>
              <th>Provider</th>
              <th>Object path</th>
              <th>Size</th>
              <th>SHA-256</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {result.artifacts.map((artifact) => {
              const linkable = isAllowlistedObjectPath(artifact.objectPath);
              const sha256 = artifact.checksums.sha256Hex;
              return (
                <tr key={artifact.id}>
                  <td>
                    <code>{artifact.kind}</code>
                  </td>
                  <td>{artifact.pythonAbi ?? '—'}</td>
                  <td>{artifact.provider}</td>
                  <td>
                    <code className="sg-path">{artifact.objectPath}</code>
                  </td>
                  <td>{formatBytes(artifact.sizeBytes)}</td>
                  <td>
                    {sha256 !== undefined ? (
                      <code title={sha256}>{sha256.slice(0, 12)}…</code>
                    ) : (
                      <span className="sg-muted">—</span>
                    )}
                  </td>
                  <td>{formatDateTime(artifact.updatedAt)}</td>
                  <td>
                    {linkable ? (
                      <button
                        type="button"
                        className="sg-button sg-button--small"
                        onClick={() => setIssueTarget(artifact)}
                      >
                        Issue link
                      </button>
                    ) : (
                      <span
                        className="sg-muted"
                        title="Only allowlisted GCS object paths (debs/…, releases/…) can be linked (spec §3)"
                      >
                        not linkable
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {issueTarget !== null && (
        <div className="sg-issue-inline">
          <h3>Issue link — {issueTarget.kind}</h3>
          <IssueLinkForm
            key={issueTarget.id}
            objectPath={issueTarget.objectPath}
            artifactId={issueTarget.id}
          />
          <div className="sg-actions">
            <button
              type="button"
              className="sg-button sg-button--small"
              onClick={() => setIssueTarget(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Page                                                                      */
/* ------------------------------------------------------------------------ */

export default function BuildRunsPage() {
  const token = useSessionToken();
  const [runs, setRuns] = useState<BuildRun[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (token === null) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    sessionApi
      .listRuns({ limit: PAGE_LIMIT }, controller.signal)
      .then((response) => {
        setRuns(response.runs);
        setNextCursor(response.nextCursor);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, reloadKey]);

  async function loadMore(): Promise<void> {
    if (nextCursor === null || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const response = await sessionApi.listRuns({ cursor: nextCursor, limit: PAGE_LIMIT });
      setRuns((previous) => [...previous, ...response.runs]);
      setNextCursor(response.nextCursor);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingMore(false);
    }
  }

  if (token === null) {
    return (
      <section className="sg-page runs-page">
        <h1>Build Runs</h1>
        <TokenSignIn />
      </section>
    );
  }

  return (
    <section className="sg-page runs-page">
      <h1>Build Runs</h1>
      <p className="sg-muted">
        GitHub Actions runs of <code>build-package-release.yml</code> with server-side caching and
        pagination. Expand a run to see its reconciled artifact inventory and issue download links.
      </p>

      <section className="sg-card">
        <div className="sg-card-header">
          <h2>Workflow runs</h2>
          <div className="sg-toolbar">
            <button
              type="button"
              className="sg-button sg-button--small"
              disabled={loading}
              onClick={() => setReloadKey((key) => key + 1)}
            >
              Refresh
            </button>
          </div>
        </div>
        <ErrorPanel error={error} onDismiss={() => setError(null)} />
        {loading ? (
          <p className="sg-muted">Loading runs…</p>
        ) : runs.length === 0 ? (
          <p className="sg-muted">No workflow runs found.</p>
        ) : (
          <div className="sg-table-wrap">
            <table className="sg-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Branch</th>
                  <th>SHA</th>
                  <th>Version</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Links</th>
                  <th>Artifacts</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <Fragment key={run.id}>
                    <tr className={expandedRunId === run.id ? 'sg-row--expanded' : ''}>
                      <td className="sg-numeric">#{run.runNumber}</td>
                      <td>{run.name ?? `run ${run.runNumber}`}</td>
                      <td>
                        <RunStatusBadge run={run} />
                      </td>
                      <td>{run.headBranch ?? '—'}</td>
                      <td>
                        <code title={run.headSha}>{run.headSha.slice(0, 7)}</code>
                      </td>
                      <td>{run.version !== null ? <code>{run.version}</code> : '—'}</td>
                      <td>{formatDateTime(run.startedAt)}</td>
                      <td>{formatDuration(run.startedAt, run.completedAt)}</td>
                      <td>
                        <a href={run.htmlUrl} target="_blank" rel="noopener noreferrer">
                          GitHub
                        </a>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="sg-button sg-button--small"
                          aria-expanded={expandedRunId === run.id}
                          onClick={() =>
                            setExpandedRunId((current) => (current === run.id ? null : run.id))
                          }
                        >
                          {expandedRunId === run.id ? 'Hide' : 'Show'}
                        </button>
                      </td>
                    </tr>
                    {expandedRunId === run.id && (
                      <tr className="sg-row--detail">
                        <td colSpan={10}>
                          <RunArtifacts run={run} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {nextCursor !== null && (
          <div className="sg-actions">
            <button
              type="button"
              className="sg-button"
              disabled={loadingMore}
              onClick={() => void loadMore()}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </section>
    </section>
  );
}
